<template>
  <div
    v-if="showModal"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto flex items-center justify-center p-4 z-50"
  >
    <div class="confirm-dialog max-w-[720px] w-full shadow-lg rounded-md bg-zyxel-gray-200 max-h-[80vh] overflow-y-auto">
      <div class="modal-header relative flex items-center p-8">
        <h2 class="text-center flex-grow">
          {{ $t("messages.menu.two_factor_auth") }}
        </h2>
        <button
          class="absolute top-0 right-0 mt-2 mr-2"
          aria-label="Close"
          tabindex="0"
          @click="closeModal"
          @keydown.enter="closeModal"
        >
          <img
            src="@/assets/svg/icon_48px_close.svg"
            alt="Close icon"
          >
        </button>
      </div>
      <div class="mb-4 px-6">
        <div class="grid grid-cols-2 bg-gray-100 border-2 border-gray-100 rounded-full">
          <button
            :class="{ 'is-active': auth_type === 0, 'cursor-pointer': true, 'rounded-full': true, 'px-2': true, 'py-2': true, 'flex': true, 'justify-center': true, 'items-center': true, 'border-2': true, 'border-transparent': true, 'transition-all': true, 'duration-200': true, 'hover:border-green-500': true, 'hover:bg-white': true }"
            tabindex="0"
            :style="{ 'border-color': auth_type === 0 ? '#006E18' : 'transparent', 'background-color': auth_type === 0 ? 'white' : 'initial' }"
            @keydown.enter="auth_type = 0"
            @click="auth_type = 0"
          >
            <img
              class="w-6 h-6"
              src="@/assets/svg/icon_48px_Google2FA.svg"
              alt=""
            >
            <div class="label b2">
              {{ $t('messages.two_factor_auth.google_title') }}
            </div>
          </button>
          <button
            :class="{ 'is-active': auth_type === 1, 'cursor-pointer': true, 'rounded-full': true, 'px-2': true, 'py-2': true, 'flex': true, 'justify-center': true, 'items-center': true, 'border-2': true, 'border-transparent': true, 'transition-all': true, 'duration-200': true, 'hover:border-green-500': true, 'hover:bg-white': true }"
            tabindex="0"
            :style="{ 'border-color': auth_type === 1 ? '#006E18' : 'transparent', 'background-color': auth_type === 1 ? 'white' : 'initial' }"
            @keydown.enter="handleEmailVerification"
            @click="handleEmailVerification"
          >
            <img
              class="w-6 h-6"
              src="@/assets/svg/icon_48px_email.svg"
              alt=""
            >
            <div class="label b2">
              {{ $t('messages.two_factor_auth.email_auth_title') }}
            </div>
          </button>
        </div>
      </div>
      <div v-if="auth_type === 0">
        <div v-if="!otpVerified">
          <div class="modal-body m-6">
            <div class="step">
              <h3>{{ $t("messages.two_factor_auth.step1") }}</h3>
              <p class="b2 text-zyxel-gray-800 mb-4">
                {{ $t("messages.two_factor_auth.step1_info") }}
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                target="_blank"
                aria-label="Google Play Store"
              ><img
                class="w-[144px] h-[44px] mb-3"
                src="@/assets/svg/app_assets_images_google_play.svg"
                alt="Google Play Store"
              ></a>
              <a
                href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8"
                target="_blank"
                aria-label="App Store"
              ><img
                class="w-[144px] h-[44px] mb-[60px]"
                src="@/assets/svg/app_assets_images_app_store.svg"
                alt="App Store"
              ></a>
            </div>
            <div class="step mb-[60px]">
              <h3>{{ $t("messages.two_factor_auth.step2") }}</h3>
              <p class="b2 text-zyxel-gray-800 mb-4">
                {{ $t("messages.two_factor_auth.step2_info") }}
              </p>

              <div v-if="showQrCode">
                <qrcode-vue
                  :value="otp.totp_uri"
                  :size="120"
                  level="H"
                  class="mb-4"
                />
                <button
                  class="b2 text-zyxel-green-700 cursor-pointer"
                  @click="toggleQrCode"
                >
                  {{ $t("messages.two_factor_auth.enter_key") }}
                </button>
              </div>
              <div v-else>
                <div class="bg-zyxel-gray-900 p-4 rounded">
                  <div class="b3 text-white">
                    {{ $t("messages.two_factor_auth.account") }}
                  </div>
                  <p class="b2 text-white mb-4">
                    {{ user.email }}
                  </p>
                  <div class="b3 text-white">
                    {{ $t("messages.two_factor_auth.key") }}
                  </div>
                  <p class="b2 text-white mb-4 flex items-center">
                    {{ otp.otp_secret }}
                    <button
                      class="text-zyxel-green-400 cursor-pointer ml-4 flex items-center"
                      @click="copyOtpSecret"
                    >
                      <template v-if="!copied">
                        <img
                          class="mr-1 h-5 w-5"
                          src="@/assets/svg/icon_copy_green.svg"
                          alt=""
                        >
                        {{ $t("messages.two_factor_auth.copy") }}
                      </template>
                      <template v-else>
                        {{ $t("messages.two_factor_auth.copied") }}
                      </template>
                    </button>
                  </p>
                </div>
                <button
                  class="b2 text-zyxel-green-700 cursor-pointer"
                  @click="toggleQrCode"
                >
                  {{ $t("messages.two_factor_auth.scan_code") }}
                </button>
              </div>
            </div>
            <div class="step mb-[20px]">
              <h3>{{ $t("messages.two_factor_auth.step3") }}</h3>
              <p class="b2 text-zyxel-gray-800 mb-4">
                {{ $t("messages.two_factor_auth.step3_info") }}
              </p>
              <label
                for="pincode"
                class="sr-only"
              >pincode</label>
              <input
                id="pincode"
                v-model="pincode"
                :class="['text-input p-4 min-w-[120px] w-full', { 'error-input': errors.code }]"
                type="text"
                inputmode="numeric"
                placeholder="Code"
              >
              <span
                v-if="errors.code"
                class="error-message mt-1 b2 block text-left w-full"
              >{{ $t(errors_msg.code) }}</span>
            </div>
          </div>
          <div class="modal-footer hidden sm:flex justify-end">
            <button
              class="mb-4 button-cancel min-w-[120px]"
              @click="cancel"
            >
              {{ $t("messages.button.cancel") }}
            </button>
            <button
              class="ml-3 mb-4 mr-4 button-primary min-w-[120px]"
              @click="verifyCode"
            >
              {{ $t("messages.button.verify") }}
            </button>
          </div>
          <div class="modal-footer flex-col sm:hidden p-6">
            <button
              class="button-primary w-full mb-4"
              @click="verifyCode"
            >
              {{ $t("messages.button.verify") }}
            </button>
            <button
              class="button-cancel w-full mb-6"
              @click="cancel"
            >
              {{ $t("messages.button.cancel") }}
            </button>
          </div>
        </div>
        <div v-else>
          <div class="modal-body m-6">
            <h3 class="mb-4">
              {{ $t("messages.two_factor_auth.otp_verified_title") }}
            </h3>
            <p class="b2 text-zyxel-gray-800 mb-4">
              {{ $t("messages.two_factor_auth.otp_verified_description") }}
            </p>
            <div class="bg-zyxel-gray-900 p-12 rounded mb-4 flex justify-center items-center">
              <div
                class="max-w-[180px] sm:max-w-[240px] md:max-w-[360px] grid grid-flow-row auto-rows-max grid-cols-[repeat(auto-fill,_minmax(60px,_1fr))] gap-2"
              >
                <div
                  v-for="( code, index ) in otp.otp_backup_codes "
                  :key="index"
                  class="text-white text-center"
                >
                  {{ code }}
                </div>
              </div>
            </div>
            <div class="flex">
              <button
                class="b2 text-zyxel-green-700 cursor-pointer"
                @click="handleDownloadBackupCodes"
              >
                {{ $t("messages.two_factor_auth.backup_code_download") }}
              </button>
              <span class="b2 text-zyxel-green-700 ml-1 mr-1">|</span>
              <button
                class="b2 text-zyxel-green-700 cursor-pointer"
                @click="handleGenerateNewBackupCodes"
              >
                {{ $t("messages.two_factor_auth.backup_code_generate") }}
              </button>
            </div>
          </div>
          <div class="modal-footer hidden sm:flex justify-end">
            <button
              class="ml-3 mb-4 mr-4 button-primary min-w-[120px]"
              @click="setOTP"
            >
              {{ $t("messages.button.ok") }}
            </button>
          </div>
          <div class="modal-footer flex-col sm:hidden p-6">
            <button
              class="button-primary w-full mb-4"
              @click="setOTP"
            >
              {{ $t("messages.button.ok") }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="auth_type === 1">
        <div class="modal-body m-6">
          <div class="bg-white rounded-lg">
            <div class="flex mb-4 text-left">
              <img
                class="ml-6 mt-6 h-12 w-12"
                src="@/assets/svg/icon_48px_send.svg"
                alt="send email"
              >
              <div class="ml-4 mt-6">
                <span class="b1 text-zyxel-gray-800 font-bold">{{ $t("messages.common.hint_send_email_to") }}</span><br>
                <span class="b1 text-zyxel-gray-800 font-bold break-all whitespace-normal">{{ user.email }}</span>
              </div>
            </div>
            <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-2 pb-6">
              {{ $t("messages.two_factor_auth.email_auth_enable") }}
            </p>
          </div>
          <div
            v-if="loading"
            class="loader"
          />
          <div v-else>
            <button
              class="b2 text-zyxel-green-700 cursor-pointer"
              @click="handleEmailVerification"
            >
              {{ $t("messages.common.link_resend_email") }}
            </button>
          </div>
        </div>
        <div class="modal-footer hidden sm:flex justify-end">
          <button
            class="ml-3 mb-4 mr-4 button-cancel min-w-[120px]"
            @click="cancel"
          >
            {{ $t("messages.button.close") }}
          </button>
        </div>
        <div class="modal-footer flex-col sm:hidden p-6">
          <button
            class="button-cancel w-full mb-4"
            @click="cancel"
          >
            {{ $t("messages.button.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import QrcodeVue from 'qrcode.vue';
import { downloadBackupCodes, generateNewBackupCodes } from '@/otpHelper.js'
import { logger } from '@/utils/logger';

export default {
    name: 'TwoFactorAuthModal',
    components: {
        QrcodeVue,
    },
    props: {
        showModal: Boolean,
        user: Object,
        otp: Object,
    },
    data() {
        return {
            pincode: "",
            showQrCode: true,
            otpVerified: false,
            emailVerified: false,
            auth_type: 0,
            copied: false,
            loading: false,
            errors: {
                "code": false
            },
            errors_msg: {
                "code": ""
            }
        }
    },
    watch: {
        showModal() {
            this.pincode = "";
            this.errors.code = false;
            this.errors_msg.code = "";
        }
    },
    methods: {
        async copyOtpSecret() {
            if (!navigator.clipboard) {
                logger.warn("此瀏覽器不支援剪貼簿功能，請手動複製。")
                return;
            }

            try {
                await navigator.clipboard.writeText(this.otp.otp_secret)
                this.copied = true
                setTimeout(() => this.copied = false, 2000) // 2秒後切回「Copy」
            } catch (err) {
                logger.error('copy fail: ', err)
            }
        },
        closeModal() {
            this.$emit('close-modal');
        },
        cancel() {
            this.closeModal();
        },
        async verifyCode() {
            const auth = useAuthStore();

            if (!this.pincode) {
                this.errors.code = true
                this.errors_msg.code = "messages.error_msg.otp_verify_failed";
                return;
            }

            try {
                let result = await auth.verifyOTPPincode(this.pincode);

                if (result) {
                    const enabled = await auth.enable2FAOTP(this.otp.otp_backup_codes);
                    if (enabled) {
                        this.otpVerified = true;
                    } else {
                        this.errors.code = true;
                        this.errors_msg.code = "messages.error_msg.otp_enable_failed";
                    }
                } else {
                    this.errors.code = true
                    this.errors_msg.code = "messages.error_msg.otp_verify_failed";
                }
            } catch (error) {
                logger.error("Error during code verification:", error);
                this.errors.code = true;
                this.errors_msg.code = "messages.error_msg.otp_verify_failed";
            }
        },
        toggleQrCode() {
            this.showQrCode = !this.showQrCode;
        },
        async handleDownloadBackupCodes() {
            await downloadBackupCodes(this.otp);
        },
        async handleGenerateNewBackupCodes() {
            const new_backup_codes = await generateNewBackupCodes();
            if (new_backup_codes) {
                this.$emit('update-otp-backup-codes', new_backup_codes);
            }
        },
        async handleEmailVerification() {
            try {
                this.loading = true;
                const auth = useAuthStore();
                const result = await auth.send2FAEmailVerification();
                if (result) {
                    this.auth_type = 1;
                    setTimeout(() => {
                        this.loading = false;
                    }, 2000);
                }
            } catch (error) {
                logger.error('Error during email verification:', error);
                this.loading = false;
            }
        },
        updateAuthType(newType) {
            this.$emit('update-auth-type', newType);
        },
        async setOTP() {
            this.closeModal();
        },
        closeOtpVerified() {
            this.otpVerified = false;
        }
    }
}
</script>